import styled, { css } from 'styled-components';
import {
    Activable,
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    getSpacingToken,
    getToken,
    IconButton,
    Image,
    ImageProps,
    MewsImage,
    StyledTransitionGroup,
    StyledTransitionGroupProps,
    Swipeable,
    TransitionGroupChildBaseProps,
    TransitionState,
} from '@optimus-web/core';

interface CarouselWrapperElementProps {
    grayscale?: boolean;
}

interface IndicatorDotProps {
    active?: boolean;
}

export const CarouselContainer = styled.div`
    width: 100%;
    height: 0;
    padding-bottom: ${(1 / (16 / 9)) * 100}%;
    position: relative;
`;

export const CarouselWrapperElement = styled.div<CarouselWrapperElementProps>`
    ${(props) =>
        props.grayscale === true &&
        css`
            filter: grayscale(1);
        `}
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

type CarouselTransitionGroup = React.ComponentType<StyledTransitionGroupProps<CarouselImageWrapperElementProps>>;

export const CarouselTransitionGroupElement = styled<CarouselTransitionGroup>(StyledTransitionGroup)`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: ${getToken('borderRadius50')}px;
`;

interface CarouselImageElementBaseProps {
    disabled?: boolean;
}

const carouselImageStyle = css<CarouselImageElementBaseProps & ImageProps>`
    ${(props) =>
        props.disabled !== true &&
        css`
            cursor: pointer;
        `}
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

export const CarouselStyledMewsImage = styled(MewsImage)`
    ${carouselImageStyle}
`;

export interface CarouselImageWrapperElementProps extends TransitionGroupChildBaseProps {
    isSlidingForward?: boolean;
}

export const CarouselImageWrapperElement = styled.a<CarouselImageWrapperElementProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: transform ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};

    &:focus {
        outline: ${getToken('borderInteractiveFocus')} solid ${getToken('borderWidth300')}px;
        outline-offset: -${getToken('borderWidth300')}px;
    }

    ${(props) =>
        props.transitionState === TransitionState.ENTERING &&
        css`
            transition: none;
            transform: translate3d(${props.isSlidingForward === true ? '' : '-'}100%, 0, 0);
        `}
    ${(props) =>
        props.transitionState === TransitionState.EXITING &&
        css`
            transform: translate3d(${props.isSlidingForward === true ? '-' : ''}100%, 0, 0);
        `}
    ${(props) =>
        props.transitionState === TransitionState.ENTERED &&
        css`
            transform: translate3d(0, 0, 0);
        `}
`;

export const CarouselImageElement = styled<React.ComponentType<CarouselImageElementBaseProps & ImageProps>>(Image)`
    ${carouselImageStyle}
`;

interface CarouselButtonProps extends Activable {
    right?: boolean;
}

export const CarouselButtonElement = styled(IconButton)<CarouselButtonProps>`
    position: absolute;
    width: ${getToken('sizing400')}px;
    height: ${getToken('sizing400')}px;
    border-radius: ${getToken('borderRadiusRound')};

    ${({ theme, right, active }) =>
        right === true &&
        css`
            right: ${active === true ? getSpacingToken('100')({ theme }) : 0}px;
        `}
    ${({ theme, right, active }) =>
        right !== true &&
        css`
            left: ${active === true ? getSpacingToken('100')({ theme }) : 0}px;
        `}

    opacity: ${(props) => (props.active === true ? 1 : 0)};
    transition: ${ANIMATION_CURVE.default} ${ANIMATION_DURATION.default}ms;
    z-index: 10;
`;

export const CarouselIndexIndicatorWrapperElement = styled.div`
    position: absolute;
    bottom: ${getSpacingToken('200')}px;
`;

export const CarouselSwipeableElement = styled(Swipeable)`
    width: 100%;
    height: 100%;
`;

export const IndexIndicatorWrapperElement = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
`;

export const IndicatorDotElement = styled.div<IndicatorDotProps>`
    width: ${getToken('sizing100')}px;
    height: ${getToken('sizing100')}px;
    margin: ${getSpacingToken('50')}px;

    opacity: ${(props) => (props.active === true ? getToken('opacity1000') : getToken('opacity600'))};
    ${(props) =>
        props.active !== true &&
        css`
            transform: scale(0.75);
        `}

    border-radius: ${getToken('borderRadiusRound')};
    background-color: ${getToken('backgroundStaticFlat')};

    transition: ${ANIMATION_CURVE.default} ${ANIMATION_DURATION.default}ms;

    ${(props) =>
        props.active !== true &&
        css`
            cursor: pointer;

            &:hover,
            &:focus,
            &:focus-visible {
                opacity: ${getToken('opacity800')};
                transform: scale(0.9);
            }
        `}
`;
